type HttpMethod = 'get' | 'post';

/**
 * Manages form input disabling based on submission method
 */
export class FormInputDisabler {
  private forms: NodeListOf<HTMLFormElement>
  private defaultMethod: HttpMethod = 'post';

  /**
   * Create a new FormInputDisabler instance
   * @param formSelector Selector for forms to manage
   */
  constructor(private formSelector: string = '[data-disable-input]') {
    this.forms = document.querySelectorAll<HTMLFormElement>(this.formSelector);
    this.initialize();
  }

  /**
   * Initialize the disabler and set up event listeners
   */
  private initialize(): void {
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      this.setupFormListeners();
    } else {
      document.addEventListener('DOMContentLoaded', () => this.setupFormListeners());
    }
  }

  /**
   * Set up submit listeners for all matched forms
   */
  private setupFormListeners(): void {
    this.forms.forEach(form => {
      form.addEventListener('submit', (event) => this.handleFormSubmit(event, form));
    });
  }

  /**
   * Handle form submission events
   */
  private handleFormSubmit(event: SubmitEvent, form: HTMLFormElement): void {
    const allowedMethod = this.getAllowedMethod(form);
    const actualMethod = this.getActualMethod(event, form);

    if (actualMethod.toLowerCase() !== allowedMethod) {
      this.disableInputs(form);
    }
  }

  /**
   * Get the allowed HTTP method for a form
   */
  private getAllowedMethod(form: HTMLFormElement): HttpMethod {
    const method = form.dataset.disableInput?.toLowerCase();
    return (method === 'get' || method === 'post') ? method : this.defaultMethod;
  }

  /**
   * Get the actual submission method being used
   */
  private getActualMethod(event: SubmitEvent, form: HTMLFormElement): string {
    const submitter = event.submitter as HTMLButtonElement;
    return submitter?.getAttribute('formmethod') || form.method;
  }

  /**
   * Disable all inputs marked for disabling in a form
   */
  private disableInputs(form: HTMLFormElement): void {
    const inputs = form.querySelectorAll<HTMLInputElement>('[data-disable]');
    inputs.forEach(input => input.disabled = true);
  }

  /**
   * Initialize with default selector
   */
  public static init(selector: string = '[data-disable-input]'): FormInputDisabler {
    return new FormInputDisabler(selector);
  }
}

// Initialize automatically when imported
FormInputDisabler.init();
